import { useState, useEffect, useRef } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { CheckCircle2, Loader2, Shield, ArrowRight, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormMessage, FormLabel } from '@/components/ui/form';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { validateIndianPhoneNumber } from '@/services/authkey';

// Phone number validation schema
const phoneSchema = z.object({
  phone: z.string()
    .min(10, 'Phone number must be at least 10 digits')
    .refine((val) => validateIndianPhoneNumber(val), {
      message: 'Please enter a valid Indian phone number',
    }),
  agreeToTerms: z.boolean().refine((val) => val === true, {
    message: 'You must agree to the Terms of Service and Privacy Policy',
  }),
});

// OTP validation schema
const otpSchema = z.object({
  otp: z.string()
    .length(6, 'OTP must be 6 digits')
    .regex(/^\d+$/, 'OTP must contain only numbers'),
});

type PhoneFormValues = z.infer<typeof phoneSchema>;
type OTPFormValues = z.infer<typeof otpSchema>;

export default function Login() {
  const { user, loading, sendOTP, verifyOTP } = useAuth();
  const [showOTPDialog, setShowOTPDialog] = useState(false);
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sendError, setSendError] = useState<string>('');
  const [otpError, setOtpError] = useState<string>('');
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Phone number form
  const phoneForm = useForm<PhoneFormValues>({
    resolver: zodResolver(phoneSchema),
    defaultValues: {
      phone: '',
      agreeToTerms: false,
    },
  });

  // OTP form
  const otpForm = useForm<OTPFormValues>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      otp: '',
    },
  });

  // No auto-reset effect here - it was wiping data on mount
  // Cleanup happens in onOTPSubmit (success) or explicitly if needed

  // Restore OTP dialog state and phone input if present (but not during auth loading)
  useEffect(() => {
    // Don't restore if user is already authenticated or auth system is loading
    if (user || loading) {
      return;
    }

    const storedOpen = sessionStorage.getItem('otp_dialog_open');
    const storedPhone = sessionStorage.getItem('otp_phone');
    const storedInput = sessionStorage.getItem('login_phone_input');
    
    // Restore OTP dialog if it was open
    if (storedOpen === 'true' && storedPhone) {
      setPhone(storedPhone);
      setShowOTPDialog(true);
    }
    
    // Restore phone input for Terms/Privacy navigation - defer to microtask to avoid flushSync warning
    if (storedInput) {
      // Schedule in a microtask to avoid React render phase updates
      Promise.resolve().then(() => {
        phoneForm.setValue('phone', storedInput, { shouldDirty: true, shouldTouch: true });
      });
    }
  }, [user, loading, phoneForm]);

  // Persist phone input as user types
  useEffect(() => {
    const subscription = phoneForm.watch((data) => {
      if (data.phone) {
        sessionStorage.setItem('login_phone_input', data.phone);
      }
    });
    return () => subscription.unsubscribe();
  }, [phoneForm.watch]);

  // Persist OTP dialog state (but not during auth loading)
  useEffect(() => {
    // Don't persist if auth system is loading or user is authenticated
    if (loading || user) {
      return;
    }
    
    sessionStorage.setItem('otp_dialog_open', showOTPDialog ? 'true' : 'false');
    if (phone) {
      sessionStorage.setItem('otp_phone', phone);
    }
  }, [showOTPDialog, phone, user, loading]);

  // Web OTP API for Android/iOS auto-fill
  useEffect(() => {
    if (!showOTPDialog) {
      // Cleanup if dialog is closed
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      return;
    }

    // Check if Web OTP API is supported (Chrome/Edge on Android, Safari on iOS)
    if ('OTPCredential' in window) {
      abortControllerRef.current = new AbortController();
      
      navigator.credentials
        .get({
          // @ts-expect-error - OTPCredential is not yet in TypeScript types
          otp: { transport: ['sms'] },
          signal: abortControllerRef.current.signal,
        })
        .then((otp: Credential | null) => {
          const otpCredential = otp as unknown as { code?: string } | null;
          if (otpCredential?.code) {
            console.log('OTP auto-detected:', otpCredential.code);
            // Fill OTP inputs
            const digits = otpCredential.code.split('');
            digits.forEach((digit: string, index: number) => {
              if (otpInputRefs.current[index]) {
                otpInputRefs.current[index]!.value = digit;
              }
            });
            // Update form
            otpForm.setValue('otp', otpCredential.code, { shouldValidate: true });
            // Focus last input
            otpInputRefs.current[5]?.focus();
          }
        })
        .catch((err: unknown) => {
          // Ignore abort errors
          if (err instanceof DOMException && err.name === 'AbortError') {
            return;
          }
          console.log('Web OTP API error:', err);
        });
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [showOTPDialog, otpForm]);

  // Ensure OTP dialog stays open on OTP errors
  useEffect(() => {
    if (otpError && !showOTPDialog) {
      const currentPhone = phone || phoneForm.getValues('phone');
      if (currentPhone) {
        setPhone(currentPhone);
      }
      setShowOTPDialog(true);
    }
  }, [otpError, showOTPDialog, phone, phoneForm]);
  // Handle OTP input change with auto-focus
  const handleOTPChange = (index: number, value: string) => {
    if (value.length === 1 && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOTPKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !e.currentTarget.value && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOTPPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const pastedData = e.clipboardData.getData('text').trim();
    // Extract only digits from pasted data
    const digits = pastedData.replace(/[^0-9]/g, '').slice(0, 6).split('');
    
    if (digits.length === 0) return;
    
    // Update all input refs
    digits.forEach((digit, index) => {
      if (otpInputRefs.current[index]) {
        otpInputRefs.current[index]!.value = digit;
      }
    });
    
    // Clear remaining inputs if paste was less than 6 digits
    for (let i = digits.length; i < 6; i++) {
      if (otpInputRefs.current[i]) {
        otpInputRefs.current[i]!.value = '';
      }
    }
    
    // Update form value with all digits
    const fullOTP = digits.join('');
    otpForm.setValue('otp', fullOTP, { shouldValidate: true });
    
    // Focus the next empty input or the last input
    const nextIndex = Math.min(digits.length, 5);
    setTimeout(() => {
      otpInputRefs.current[nextIndex]?.focus();
    }, 0);
  };

  const handleOTPInput = (index: number, e: React.FormEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    const value = target.value;
    
    // Handle paste into input (Android fallback)
    if (value.length > 1) {
      const digits = value.replace(/[^0-9]/g, '').slice(0, 6).split('');
      
      // Fill all inputs starting from current index
      digits.forEach((digit, i) => {
        const inputIndex = index + i;
        if (inputIndex < 6 && otpInputRefs.current[inputIndex]) {
          otpInputRefs.current[inputIndex]!.value = digit;
        }
      });
      
      // Update form value
      const otpValues = otpInputRefs.current
        .map((ref) => ref?.value || '')
        .join('');
      otpForm.setValue('otp', otpValues, { shouldValidate: true });
      
      // Focus appropriate input
      const nextIndex = Math.min(index + digits.length, 5);
      setTimeout(() => {
        otpInputRefs.current[nextIndex]?.focus();
      }, 0);
      
      return;
    }
    
    // Normal single character input
    const digit = value.replace(/[^0-9]/g, '');
    target.value = digit;
    
    if (digit && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
    
    // Update form value
    const otpValues = otpInputRefs.current
      .map((ref) => ref?.value || '')
      .join('');
    otpForm.setValue('otp', otpValues, { shouldValidate: true });
  };

  // Handle phone number submission
  const onPhoneSubmit = async (data: PhoneFormValues) => {
    try {
      setIsLoading(true);
      setSendError('');
      setOtpError('');
      await sendOTP(data.phone);
      setPhone(data.phone);
      setShowOTPDialog(true);
    } catch (err) {
      console.error('Failed to send OTP:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to send OTP. Please try again.';
      setSendError(errorMessage);
      // Don't show OTP dialog on error - let user see the authentication error
    } finally {
      setIsLoading(false);
    }
  };

  // Handle OTP verification
  const onOTPSubmit = async (data: OTPFormValues) => {
    try {
      setIsLoading(true);
      setOtpError('');
      
      // Clear sessionStorage immediately to prevent restoration
      sessionStorage.removeItem('otp_dialog_open');
      sessionStorage.removeItem('otp_phone');
      sessionStorage.removeItem('login_phone_input');
      
      // verifyOTP will handle loading state via AuthContext
      await verifyOTP(data.otp);
      // On success, AuthContext sets user and loading goes false
    } catch (err) {
      console.error('Failed to verify OTP:', err);
      const errorMessage = err instanceof Error ? err.message : 'Invalid OTP. Please try again.';
      setOtpError(errorMessage);
      
      // Keep OTP dialog open and preserve phone
      const currentPhone = phone || phoneForm.getValues('phone');
      if (currentPhone) {
        setPhone(currentPhone);
      }
      setShowOTPDialog(true);
      sessionStorage.setItem('otp_dialog_open', 'true');
      if (currentPhone) {
        sessionStorage.setItem('otp_phone', currentPhone);
      }

      // Don't clear OTP - let user see what they entered and correct it
      setTimeout(() => {
        otpInputRefs.current[0]?.focus();
        otpInputRefs.current[0]?.select();
      }, 100);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle resend OTP
  const handleResendOTP = async () => {
    try {
      setIsLoading(true);
      setSendError('');
      setOtpError('');
      otpForm.reset();
      await sendOTP(phone);
    } catch (err) {
      console.error('Failed to resend OTP:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to resend OTP. Please try again.';
      setSendError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

    // If this is an active verification process (user submitted OTP), show loading
    // But for initial page load, just show the form immediately
    if (isLoading) {
      return (
        <div className="min-h-screen min-h-dvh flex items-center justify-center bg-gradient-to-br from-slate-50 via-slate-50/95 to-teal-50/30">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
            <p className="text-slate-500 font-medium text-sm">Signing you in...</p>
          </div>
        </div>
      );
    }

    // Redirect if already authenticated
    if (user && !loading) {
      return <Navigate to="/dashboard" replace />;
    }

    return (
      <div className="min-h-screen min-h-dvh flex flex-col bg-gradient-to-br from-slate-50 via-slate-50/95 to-teal-50/30 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Main gradient orbs */}
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-teal-400/20 to-cyan-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
          <div className="absolute -bottom-40 -left-40 w-[700px] h-[700px] bg-gradient-to-tr from-blue-900/10 to-indigo-900/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s' }} />
          <div className="absolute top-1/3 left-1/3 w-[500px] h-[500px] bg-gradient-to-bl from-teal-300/10 to-emerald-300/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s' }} />
          
          {/* Subtle grid pattern */}
          <div className="absolute inset-0 opacity-[0.02]" style={{ 
            backgroundImage: `radial-gradient(circle at 1px 1px, rgb(0 0 0 / 0.15) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }} />
        </div>

        {/* Content Container */}
        <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-6 relative z-10">
          {/* Logo/Brand Section */}
          <div className="mb-10 text-center space-y-3 animate-in fade-in slide-in-from-top-4 duration-700">
            <div className="inline-flex items-center justify-center w-[88px] h-[88px] rounded-[24px] bg-gradient-to-br from-[#2d5a6b] via-[#3a7680] to-[#4ea39f] shadow-2xl shadow-teal-500/30 mb-3 relative group">
              {/* Subtle glow effect */}
              <div className="absolute inset-0 rounded-[24px] bg-gradient-to-br from-teal-400/40 to-cyan-400/40 blur-xl opacity-60 group-hover:opacity-80 transition-opacity" />
              <span className="text-5xl font-bold text-white relative z-10 drop-shadow-lg">D</span>
            </div>
            <div>
              <h1 className="text-[42px] font-extrabold bg-gradient-to-r from-[#2d5a6b] via-[#3a7680] to-[#4ea39f] bg-clip-text text-transparent tracking-tight leading-none">
                DsaLens
              </h1>
              <p className="text-[13px] text-slate-500 mt-2 flex items-center justify-center gap-1.5 font-medium">
                <Sparkles className="w-3.5 h-3.5 text-teal-500" />
                DSA Partner Portal
              </p>
            </div>
          </div>

          {/* Main Login Card */}
          <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-4 duration-700" style={{ animationDelay: '150ms' }}>
            <div className="bg-white/95 backdrop-blur-2xl rounded-[28px] shadow-[0_20px_70px_-15px_rgba(0,0,0,0.15)] border border-slate-200/60 p-8 space-y-6 relative overflow-hidden">
              {/* Subtle top gradient accent */}
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-teal-500/60 to-transparent" />
              {/* Subtle top gradient accent */}
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-teal-500/60 to-transparent" />
              
              {/* Title Section */}
              <div className="space-y-2 relative">
                <h2 className="text-[26px] font-bold text-slate-900 tracking-tight">
                  Welcome Back! 👋
                </h2>
                <p className="text-slate-500 text-[15px] font-medium">
                  Enter your phone number to continue
                </p>
              </div>

              {/* Phone Form */}
              <Form {...phoneForm}>
                <form onSubmit={phoneForm.handleSubmit(onPhoneSubmit)} className="space-y-5">
                  <FormField
                    control={phoneForm.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <div className="relative group">
                            {/* Glow effect on focus */}
                            <div className="absolute -inset-[1px] bg-gradient-to-r from-teal-500/30 to-cyan-500/30 rounded-[18px] opacity-0 group-focus-within:opacity-100 blur-sm transition-opacity duration-300" />
                            
                            <div className="relative flex items-center bg-slate-50/80 rounded-[18px] border-2 border-slate-200 focus-within:border-teal-500/60 focus-within:bg-white transition-all duration-200 hover:border-slate-300 shadow-sm">
                              <span className="pl-5 pr-3 text-slate-600 font-semibold text-[15px] flex items-center gap-1.5">
                                🇮🇳 <span className="text-slate-400">+91</span>
                              </span>
                              <div className="h-7 w-px bg-slate-200" />
                              <Input
                                {...field}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '');
                                  field.onChange(value);
                                }}
                                type="tel"
                                placeholder=""
                                className="border-0 h-[58px] text-[17px] font-semibold text-slate-900 placeholder:text-slate-400 placeholder:font-normal focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent"
                                disabled={isLoading}
                                maxLength={10}
                              />
                            </div>
                          </div>
                        </FormControl>
                        <FormMessage className="text-xs font-medium mt-2" />
                      </FormItem>
                    )}
                  />

                  {sendError && (
                    <Alert variant="destructive" className="rounded-xl border-red-200 bg-red-50/80">
                      <AlertDescription className="text-sm font-medium text-red-800">{sendError}</AlertDescription>
                    </Alert>
                  )}

                  {/* Terms and Privacy Policy Checkbox */}
                  <FormField
                    control={phoneForm.control}
                    name="agreeToTerms"
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <div className="flex items-start gap-3">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              className="mt-0.5"
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="text-[13px] text-slate-600 font-normal leading-relaxed cursor-pointer">
                              I agree to the{' '}
                              <Link
                                to="/terms-of-service"
                                className="text-teal-600 hover:text-teal-700 font-semibold underline underline-offset-2"
                              >
                                Terms of Service
                              </Link>
                              {' '}and{' '}
                              <Link
                                to="/privacy-policy"
                                className="text-teal-600 hover:text-teal-700 font-semibold underline underline-offset-2"
                              >
                                Privacy Policy
                              </Link>
                              , including the confidentiality agreement that prohibits taking screenshots or sharing any information from this application.
                            </FormLabel>
                          </div>
                        </div>
                        <FormMessage className="text-xs font-medium" />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="w-full h-[58px] text-[16px] font-bold rounded-[18px] bg-gradient-to-r from-[#2d5a6b] via-[#3a7680] to-[#4ea39f] hover:shadow-xl hover:shadow-teal-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] shadow-lg"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Sending OTP...
                      </>
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                </form>
              </Form>

              {/* Security Badge */}
              <div className="flex items-center justify-center gap-2 pt-4 border-t border-slate-100">
                <Shield className="w-4 h-4 text-teal-500" />
                <span className="text-[13px] text-slate-500 font-medium">
                  Secure & encrypted login
                </span>
              </div>
            </div>
          </div>

          {/* Footer Info - Confidentiality Reminder */}
          <div className="mt-6 max-w-md mx-auto">
            <div className="bg-red-50/80 backdrop-blur-sm border border-red-200/60 rounded-2xl p-4">
              <p className="text-[11px] text-red-700 text-center font-semibold leading-relaxed">
                ⚠️ CONFIDENTIAL: This application is for internal use only. Screenshots and information sharing are strictly prohibited.
              </p>
            </div>
          </div>
        </div>

        {/* OTP Verification Modal */}
        {showOTPDialog && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-[28px] shadow-[0_30px_90px_-15px_rgba(0,0,0,0.35)] border border-slate-200/60 w-full max-w-md p-8 space-y-6 animate-in slide-in-from-bottom-6 duration-500 relative overflow-hidden">
              {/* Top gradient accent */}
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-teal-500/60 to-transparent" />
              
              {/* OTP Header */}
              <div className="text-center space-y-4">
                <div className="inline-flex items-center justify-center w-[72px] h-[72px] rounded-[20px] bg-gradient-to-br from-teal-50 to-cyan-50 border-2 border-teal-100 mb-2 relative group">
                  <div className="absolute inset-0 rounded-[20px] bg-gradient-to-br from-teal-200/40 to-cyan-200/40 blur-xl opacity-50 group-hover:opacity-70 transition-opacity" />
                  <CheckCircle2 className="w-9 h-9 text-teal-600 relative z-10" />
                </div>
                <div>
                  <h3 className="text-[24px] font-bold text-slate-900 tracking-tight">
                    Verify OTP
                  </h3>
                  <p className="text-[14px] text-slate-500 mt-2 font-medium">
                    Code sent to <span className="font-bold text-slate-900">+91 {phone}</span>
                  </p>
                </div>
              </div>

              {/* OTP Form */}
              <Form {...otpForm}>
                <form onSubmit={otpForm.handleSubmit(onOTPSubmit)} className="space-y-6">
                  <FormField
                    control={otpForm.control}
                    name="otp"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <div className="space-y-3">
                            <div className="flex gap-2.5 justify-center">
                              {[0, 1, 2, 3, 4, 5].map((index) => (
                                <input
                                  key={index}
                                  ref={(el) => (otpInputRefs.current[index] = el)}
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={6}
                                  autoComplete={index === 0 ? 'one-time-code' : 'off'}
                                  className="w-[48px] h-[56px] sm:w-[52px] sm:h-[60px] text-center text-[24px] font-bold rounded-[14px] border-2 border-slate-200 bg-slate-50/50 focus:border-teal-500 focus:bg-white focus:ring-4 focus:ring-teal-500/20 transition-all disabled:opacity-50 shadow-sm hover:border-slate-300"
                                  onInput={(e) => handleOTPInput(index, e)}
                                  onKeyDown={(e) => handleOTPKeyDown(index, e)}
                                  onPaste={handleOTPPaste}
                                  disabled={isLoading}
                                  autoFocus={index === 0}
                                />
                              ))}
                            </div>
                          </div>
                        </FormControl>
                        <FormMessage className="text-center text-xs font-medium mt-3" />
                      </FormItem>
                    )}
                  />

                  {otpError && (
                    <Alert variant="destructive" className="rounded-xl border-red-200 bg-red-50/80">
                      <AlertDescription className="text-sm font-medium text-red-800">{otpError}</AlertDescription>
                    </Alert>
                  )}

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <Button
                      type="submit"
                      className="w-full h-[58px] text-[16px] font-bold rounded-[18px] bg-gradient-to-r from-[#2d5a6b] via-[#3a7680] to-[#4ea39f] hover:shadow-xl hover:shadow-teal-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] shadow-lg"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          Verify & Continue
                          <CheckCircle2 className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>

                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleResendOTP}
                      disabled={isLoading}
                      className="w-full h-12 rounded-[14px] hover:bg-teal-50 hover:text-teal-700 transition-colors font-semibold text-slate-600"
                    >
                      Resend OTP
                    </Button>
                  </div>

                  {/* Change Number */}
                  <button
                    type="button"
                    onClick={() => {
                      setShowOTPDialog(false);
                      setOtpError('');
                      setPhone('');
                      otpForm.reset();
                      otpInputRefs.current.forEach((ref) => {
                        if (ref) ref.value = '';
                      });
                      // Clear OTP sessionStorage
                      sessionStorage.removeItem('otp_dialog_open');
                      sessionStorage.removeItem('otp_phone');
                    }}
                    disabled={isLoading}
                    className="w-full text-[13px] text-slate-500 hover:text-slate-900 transition-colors pt-2 font-medium"
                  >
                    ← Change Phone Number
                  </button>
                </form>
              </Form>
            </div>
          </div>
        )}
      </div>
    );
  }
